import { withNamespaces } from "react-i18next";
import { connect } from "react-redux";
import { Fragment, memo, useCallback, useEffect, useMemo, useState } from "react";
import { Col, Row } from "reactstrap";
import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import OverviewHeader from "../../../overview-header";
import CommonService from "src/modules/data-protection/apis/CommonService";
import ReportsService from "src/modules/data-protection/apis/ReportsService";
import CustomTable from "src/components/CustomTable";
import Pagination from "src/modules/data-protection/components/pagination";
import PageBody from "src/modules/data-protection/components/page-body";
import Deletion from "src/modules/data-protection/pages/admin/other-assets/modal/deletion";
import { withRouter } from "react-router-dom";
import DateUtils from "src/services/utils/DateUtils";
import { REPORT_STATUS_LIST } from "src/components/constants";
import FilteringComponent from "./components/filtering-component";
import { displayedCaseReference } from "src/helpers/case_reference_helper";

import "./style.scss";

const OtherAssetRelatedIncidents = (props) => {
    const location = useLocation();

    const dateUtils = new DateUtils();
    const { t, assetId } = props;

    const [showDeletionModal, setDeletionModal] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [deletionComplete, setDeletionComplete] = useState(false);

    const [tableFilters, setTableFilters] = useState({
        pageIndex: 1,
        pageSize: 25,
        adminCategories: null,
        status: null,
        risk: null,
        analystAssigned: null,
        sortField: null,
        sortOrder: null
    });

    const tableHeader = [
        [
            {
                value: (
                    <p className="text-truncate text-capitalize text-left">
                        {props.t("Risk Rating")}
                    </p>
                ),
                width: "5%",
            },
            {
                value: (
                    <p className="text-truncate text-capitalize text-left">
                        {props.t("Incident ID")}
                    </p>
                ),
                width: "10%",
            },
            {
                value: (
                    <p className="text-truncate text-capitalize text-center">
                        {props.t("Description")}
                    </p>
                ),
                width: "10%",
            },
            {
                value: (
                    <p className="text-truncate text-capitalize text-center">
                        {props.t("Submission Date")}
                    </p>
                ),
                width: "10%",
            },
            {
                value: (
                    <p className="text-truncate text-capitalize text-center">
                        {props.t("Closing Date")}
                    </p>
                ),
                width: "10%",
            },
            {
                value: (
                    <p className="text-truncate text-capitalize text-center">
                        {props.t("Assign Analyst")}
                    </p>
                ),
                width: "10%",
            },
            {
                value: (
                    <p className="text-truncate text-capitalize text-center">
                        {props.t("Status")}
                    </p>
                ),
                width: "10%",
            },
        ],
    ];

    const handleFetchIncidentListQuery = useQuery({
        queryKey: ["data-protection-other-assets-incidents-list", assetId, tableFilters],
        queryFn: async () => {
            const service = ReportsService.getInstance();
            return await service.list({
                ...tableFilters,
                otherAsset: Number(assetId)
            });
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (error) => {
            if (process.env.NODE_ENV === "development") console.error(error);

            toast(t("An error occurred while fetching incidents."), {
                type: "error",
            });
        },
    });

    const fetchUsers = useQuery({
        queryFn: async () => {
            const service = CommonService.getInstance();

            return await service.analystAdminsAndAnalysts();
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (error) => {
            if (process.env.NODE_ENV === "development") console.error(error);

            toast(t("An error occurred while fetching users."), {
                type: "error",
            });
        },
    });

    const handleRenderReportStatus = (statusId) => {
        const status = REPORT_STATUS_LIST.find(
            (item) => item.id === parseInt(statusId)
        );
        return (
            <div className={`${status.className} p-2`} style={{ minWidth: "78px" }}>
                {t(status.titleKey)}
            </div>
        );
    };

    const handleProvideTableData = () => {
        return (handleFetchIncidentListQuery.data?.reports || []).map((incident) => {
            return [
                {
                    value: (
                        <p className="text-truncate text-capitalize">
                            <span
                                style={{ backgroundColor: `#${incident.risk}` }}
                                className="risk-rate ms-5"
                            ></span>
                        </p>
                    ),
                    width: "5%",
                    isRecentlyAdded: incident?.isRecentlyAdded,
                },
                {
                    value: (
                        <Link
                            to={`/admin/data-protection/incident-register/details/${incident?.case_id}`}
                            onClick={() => localStorage.setItem("caseId", incident?.case_id)}
                        >
                            <p className="text-truncate text-capitalize text-left">
                                {displayedCaseReference(incident) || ""}
                            </p>
                        </Link>
                    ),
                    width: "10%",
                    isRecentlyAdded: incident?.isRecentlyAdded,
                },
                {
                    value: (
                        <p className="text-truncate text-capitalize text-center">
                            {/* {incident?.admin_description  || "" } */}
                            {"admin_description" in incident &&
                                incident?.admin_description.trim()
                                ? incident?.admin_description.length > 70
                                    ? `${incident?.admin_description?.substring(0, 70)}...`
                                    : incident?.admin_description
                                : incident?.description
                                    ? incident?.description.length > 70
                                        ? `${incident?.description.substring(0, 70)}...`
                                        : incident?.description
                                    : "---"}
                        </p>
                    ),
                    width: "10%",
                    isRecentlyAdded: incident?.isRecentlyAdded,
                },
                {
                    value: (
                        <p className="text-truncate text-capitalize text-center d-flex justify-content-center">
                            {dateUtils.convertTimestampToOnlyDate(
                                incident?.submission_date
                            ) || ""}
                        </p>
                    ),
                    width: "10%",
                    isRecentlyAdded: incident?.isRecentlyAdded,
                },
                {
                    value: (
                        <p className="text-truncate text-capitalize text-center d-flex justify-content-center">
                            {incident?.closing_date
                                ? dateUtils.convertTimestampToOnlyDate(incident?.closing_date)
                                : ""}
                        </p>
                    ),
                    width: "10%",
                    isRecentlyAdded: incident?.isRecentlyAdded,
                },
                {
                    value: (
                        <p className="text-truncate text-capitalize text-center">
                            {incident?.analyst_assigned.id
                                ? `${incident?.analyst_assigned.first_name} ${incident?.analyst_assigned.last_name}`
                                : ""}
                        </p>
                    ),
                    width: "10%",
                    isRecentlyAdded: incident?.isRecentlyAdded,
                },
                {
                    value: (
                        <p className="text-truncate text-capitalize text-center">
                            <div>{handleRenderReportStatus(incident?.status)}</div>
                        </p>
                    ),
                    width: "10%",
                    isRecentlyAdded: incident?.isRecentlyAdded,
                },
            ];
        });
    };

    const usersList = useMemo(() => {
        return (fetchUsers.data || []).map((u) => {
            return {
                value: u.id,
                label: u.first_name + " " + u.last_name,
            };
        });
    }, [
        fetchUsers.data
    ]);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);

        const pageIndex = searchParams.get("pageIndex");
        if (pageIndex !== null && !Number.isNaN(Number(pageIndex)))
            setTableFilters((prevFilters) => ({
                ...prevFilters,
                pageIndex: Number(pageIndex)
            }))

        const pageSize = searchParams.get("pageSize");
        if (pageSize !== null && !Number.isNaN(Number(pageSize)))
            setTableFilters((prevFilters) => ({
                ...prevFilters,
                pageSize: Number(pageSize)
            }))
    }, [location.search]);

    useEffect(() => {
        if (deletionComplete) {
            handleFetchIncidentListQuery.refetch();
            setDeletionComplete(false);
        }
    }, [deletionComplete]);

    const handleFilterChange = useCallback((newFilters) => {
        setTableFilters((prevFilters) => {
            return {
                ...prevFilters,
                ...newFilters
            }
        })
    }, []);

    return (
        <>
            <div className="p-4">
                <Row>
                    <Col className="pb-4">
                        <OverviewHeader
                            assetId={assetId}
                        />
                    </Col>
                </Row>
                <div className="d-flex mb-4 justify-content-end w-full gap-2">
                    <FilteringComponent 
                        onChange={handleFilterChange}
                    />

                    <Link
                        className="btn btn-primary"
                        to={`/admin/data-protection/incident-register?otherAssetId=${assetId}`}
                    >
                        {props.t("Add A New Incident")}
                    </Link>

                </div>

                {/* page body */}
                <PageBody classNames="box-shadow">
                    {!handleFetchIncidentListQuery.isLoading ||
                        !handleFetchIncidentListQuery.isFetching ? (
                        !handleFetchIncidentListQuery.isError ? (
                            handleFetchIncidentListQuery.data?.reports?.length ? (
                                // data
                                <Fragment>
                                    <Row>
                                        <Col sm="12" md="12" lg="12">
                                            <CustomTable
                                                headerData={tableHeader}
                                                data={handleProvideTableData()}
                                            />
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col sm="12" md="12" lg="12">
                                            <Pagination
                                                pageIndex={tableFilters.pageIndex}
                                                pageSize={tableFilters.pageSize}
                                                totalCount={handleFetchIncidentListQuery.data?.itemsCount || 0}
                                                link={location.pathname}
                                            />
                                        </Col>
                                    </Row>
                                </Fragment>
                            ) : (
                                // empty
                                <Row>
                                    <Col sm="12" md="12" lg="12">
                                        <Fragment>
                                            <CustomTable headerData={tableHeader} data={[]} />
                                            <div className="p-2">
                                                <div className="alert alert-secondary text-center">
                                                    {props.t("This List Is Empty.")}
                                                </div>
                                            </div>
                                        </Fragment>
                                    </Col>
                                </Row>
                            )
                        ) : (
                            // error
                            <Row>
                                <Col sm="12" md="12" lg="12">
                                    <Fragment>
                                        <CustomTable headerData={tableHeader} data={[]} />
                                        <div className="p-2">
                                            <div className="alert alert-danger text-center">
                                                {props.t("Failed To Fetch Incidents.")}
                                            </div>
                                        </div>
                                    </Fragment>
                                </Col>
                            </Row>
                        )
                    ) : (
                        // loading
                        <Row>
                            <Col sm="12" md="12" lg="12">
                                <Fragment>
                                    <CustomTable headerData={tableHeader} data={[]} />
                                    <div className="p-2">
                                        <div className="alert alert-info text-center">
                                            {props.t("Loading")} ...
                                        </div>
                                    </div>
                                </Fragment>
                            </Col>
                        </Row>
                    )}
                </PageBody>
            </div>

            {/* Deletion Modal */}
            {usersList?.length && (
                <Deletion
                    t={t}
                    modal={showDeletionModal}
                    setModal={setDeletionModal}
                    users={usersList}
                    id={selectedId}
                    setDeletionComplete={setDeletionComplete}
                />
            )}
            {/* </PageContent> */}
        </>
    );
};

export default withNamespaces()(
    withRouter(
        connect(null, { })(memo(OtherAssetRelatedIncidents))
    )
);
